import type {ProcessFindingsData} from "../../types";

export const PROCESS_FINDINGS: ProcessFindingsData[] = [
  {
    processId: "proc-vendor-invoice",
    findings: [
      {
        id: "find-vi-01",
        rating: "high",
        internal: {
          title: "Segregation of duties gap in purchase requisition approval",
          content:
            "During testing of control AP-01, we identified **3 out of 25 sampled transactions** where the purchase requisition was approved by the same individual who initiated the request. The current system configuration does not enforce segregation between requisition initiation and approval roles.\n\n**Root cause:** The ERP role assignment allows users with the 'Procurement Specialist' role to both create and approve requisitions below the $10,000 threshold.\n\n**Impact:** Without proper segregation of duties, there is an increased risk of unauthorized or fraudulent purchases being processed without independent oversight.",
        },
        clientFacing: {
          title: "Purchase requisition approval segregation of duties",
          content:
            "We identified instances where purchase requisitions were approved by the same individual who initiated the request. We recommend implementing system-enforced segregation of duties controls for all purchase requisition approvals regardless of dollar threshold.",
        },
        controlId: "ctrl-vi-01a",
        includeInternal: true,
        includeClientFacing: true,
        status: "in-progress",
      },
      {
        id: "find-vi-02",
        rating: "moderate",
        internal: {
          title: "Three-way match tolerance override without documented justification",
          content:
            "In **7 out of 40 sampled transactions**, the three-way match tolerance was manually overridden to process payment. While the overrides were approved by an AP supervisor, **4 of the 7 lacked documented business justification** for the variance between the PO, goods receipt, and invoice amounts.\n\n**Root cause:** The override approval workflow does not include a mandatory justification field.\n\n**Impact:** Payments may be processed for incorrect amounts without adequate documentation to support the variance, increasing the risk of overpayment or fraudulent billing.",
        },
        clientFacing: {
          title: "Three-way match override documentation",
          content:
            "We noted instances where three-way match tolerances were overridden without sufficient documentation of the business justification. We recommend enhancing the override workflow to require mandatory documentation of the reason for variance acceptance.",
        },
        controlId: "ctrl-vi-02a",
        includeInternal: true,
        includeClientFacing: true,
        status: "in-review",
      },
      {
        id: "find-vi-03",
        rating: "low",
        internal: {
          title: "Delayed GL account validation for intercompany transactions",
          content:
            "GL account validation for intercompany transactions is performed **monthly during the close process** rather than at the point of invoice entry. Testing identified that **12 transactions** were initially posted to incorrect intercompany accounts and required correcting journal entries during month-end close.\n\n**Root cause:** The automated GL validation rules do not include intercompany account codes in the real-time validation check.\n\n**Impact:** While errors are ultimately corrected during close, the delayed detection increases close cycle time and the risk of intercompany account misstatements in interim financial reporting.",
        },
        clientFacing: null,
        controlId: "ctrl-vi-03a",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
    ],
  },
  {
    processId: "proc-journal-entry",
    findings: [
      {
        id: "find-je-01",
        rating: "moderate",
        internal: {
          title: "Recurring journal entry templates not subject to periodic re-authorization",
          content:
            "Recurring journal entry templates are set up during initial configuration but are **not subject to periodic review or re-authorization**. We identified **8 active recurring templates** that have been in use for over 18 months without any documented review of their continued appropriateness or accuracy of amounts.\n\n**Root cause:** There is no established policy or system reminder for periodic review of recurring journal entry templates.\n\n**Impact:** Outdated or incorrect recurring entries may continue to post without detection, potentially resulting in misstatements that accumulate over multiple periods.",
        },
        clientFacing: {
          title: "Periodic review of recurring journal entry templates",
          content:
            "We noted that recurring journal entry templates are not subject to periodic re-authorization or review. We recommend establishing a quarterly review process for all active recurring templates to confirm continued appropriateness and accuracy of recorded amounts.",
        },
        controlId: null,
        includeInternal: true,
        includeClientFacing: true,
        status: "in-review",
      },
    ],
  },
];
